import logger from "@/lib/logger";
import { auth } from "@/stores/auth.store";
import "@mdi/font/css/materialdesignicons.css";
import urql from "@urql/vue";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { createPinia } from "pinia";
import { createApp } from "vue";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import {
  aliases,
  mdi,
} from "vuetify/iconsets/mdi";
import "vuetify/styles";
import App from "./app/App.vue";
import "./design/styles/index.scss";
import { createExchanges } from "./exchanges/CreateExchanges";
import router from "./router";
import { VueNavigationClient } from "./router/helpers";
import { useUserStore } from "./stores/user.store";
import "./splunk-instrumentation.ts";
import { useFragment, UserMainFieldsFragmentDoc, type UserOverviewFragment } from "owfsa-graphql";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Edmonton");

// log level
localStorage.setItem("debug", import.meta.env.VITE_DEBUG || "*");

const app = createApp(App);
export const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: "mdi",
    aliases,
    sets: {
      mdi,
    },
  },
  defaults: {
    VCombobox: {
      autocomplete: "off",
    },
    VSelect: {
      autocomplete: "off",
    },
    VAutocomplete: {
      autocomplete: "off",
    },
    VTextField: {
      autocomplete: "off",
    },
  },
});

app.config.errorHandler = (err, vm, info) => {
  logger.error("Error:", err);
  logger.error("Vue component:", vm);
  logger.error("Additional info:", info);
};

app.use(createPinia());

const urqlExchanges = createExchanges();

app.use(urql, {
  fetchOptions: {
    headers: {},
  },
  url: import.meta.env.VITE_API_ROOT,
  exchanges: import.meta.env.DEV ? urqlExchanges : urqlExchanges.slice(1),
  preferGetMethod: false,
});

window.onerror = function (msg) {
  if (typeof msg === "string") {
    if (msg.startsWith("ResizeObserver")) {
      // Ignore this error as it is a known issue with Chrome and does not affect our app's functionality
      return;
    }
  }
  logger.error(`Uncaught error: ${JSON.stringify(msg)}`);
};

window.addEventListener("unhandledrejection", function (event) {
  logger.error(`Uncaught rejection: ${event.reason}`);
});

const client = new VueNavigationClient(router);

router.beforeEach(async (to) => {
  const userStore = useUserStore();

  if (!auth.initialized) {
    await auth.initialize(client);
  }

  if (to.meta?.requiresAuth) {
    // authorised
    if (auth.account) {
      try {
        // Only fetch user data if we don't have it yet
        if (userStore.self === null) {
          const result = await userStore.getSelfQuery.executeQuery({
            requestPolicy: "network-only",
          });
          const selfData = useFragment(UserMainFieldsFragmentDoc, result.data.value?.self) as unknown as UserOverviewFragment;
          userStore.self = selfData;
        }

        const selfData = userStore.self;

        // deleted/suspended
        if (
          selfData?.isDeleted ||
          selfData?.isSuspended
        ) {
          auth.logout();
          return"/";
        }

        if (to.meta.checkRoles()) {
          return true;
        }

        return"/";
      } catch (error) {
        logger.error("Navigation guard error:", error);
        return "/";
      }
    }

    // unauthorised
    try {
      await auth.login();
      return "/";
    } catch (error) {
      logger.error("Unauthorized:", error);
      return false;
    }
  } else {
    return true;
  }
});

app.use(router);
app.use(vuetify);

app.mount("#app");
